import { SUPPORTED_LOCALE_CODES } from './locales';

// eslint-disable-next-line i18next/no-literal-string -- BCP-47 tag, not copy
export const FALLBACK_LOCALE = 'en';

// A Set rather than an object lookup: a corrupt stored value like `toString`
// resolves through an object's prototype and would pass as supported.
const supported = new Set(SUPPORTED_LOCALE_CODES);

export function isSupportedLocale(value: unknown): value is string {
  return typeof value === 'string' && supported.has(value);
}

function baseTag(code: string): string {
  return code.split('-')[0].toLowerCase();
}

export interface LocaleSources {
  stored?: string | null;
  cached?: string | null;
  /** `navigator.languages`, in the browser's own order of preference. */
  preferred?: readonly string[];
}

/**
 * The precedence agreed on the ticket: user record, browser cache, browser
 * languages, English. Reordering these tiers changes which device wins.
 */
export function resolveLocale({ stored, cached, preferred = [] }: LocaleSources): string {
  if (isSupportedLocale(stored)) return stored;
  if (isSupportedLocale(cached)) return cached;

  for (const candidate of preferred) {
    if (isSupportedLocale(candidate)) return candidate;

    // `en-GB` should land on `en` rather than English-speaking users getting the
    // fallback by accident. Browsers also disagree on the casing they report.
    const sharingBase = SUPPORTED_LOCALE_CODES.find((code) => baseTag(code) === baseTag(candidate));
    if (sharingBase) return sharingBase;
  }

  return FALLBACK_LOCALE;
}
